import { v4 as uuidv4 } from 'uuid';
import { Utils } from '@client/utility';

interface UploadResult {
  url: string | null;
  error: string | null;
}

const MAX_DIMENSION = 1024;

export async function uploadImage(
  bucket: string,
  file: File | Blob
): Promise<UploadResult> {
  try {
    if (!file.type.startsWith('image/') && !(file instanceof Blob)) {
      return { url: null, error: 'Only image files or blobs are allowed.' };
    }

    const maxSizeInBytes = 5 * 1024 * 1024;
    if ('size' in file && file.size > maxSizeInBytes) {
      return { url: null, error: 'File size must be <= 5MB.' };
    }

    const avifBlob = await convertToAvif(file);
    if (!avifBlob || avifBlob.size > maxSizeInBytes) {
      return { url: null, error: 'Converted image size must be <= 5MB.' };
    }

    const arrayBuffer = await avifBlob.arrayBuffer();
    const base64File = arrayBufferToBase64(arrayBuffer);

    const response = await Utils.post<{ url: string }>('/api/uploadImage', {
      bucket,
      file: base64File,
      fileName: `${uuidv4()}.avif`,
    });

    if (!response?.url) {
      return { url: null, error: 'Failed to get uploaded image URL.' };
    }

    return { url: response.url, error: null };
  } catch (error: any) {
    return { url: null, error: error.message || 'Unexpected error occurred.' };
  }
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const slice = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...slice);
  }
  return btoa(binary);
}

async function convertToAvif(input: File | Blob): Promise<Blob | null> {
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(input);
    const img = new Image();

    img.onload = () => {
      try {
        let { width, height } = img;
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          const aspectRatio = width / height;
          if (aspectRatio > 1) {
            width = MAX_DIMENSION;
            height = Math.round(MAX_DIMENSION / aspectRatio);
          } else {
            height = MAX_DIMENSION;
            width = Math.round(MAX_DIMENSION * aspectRatio);
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(null);

        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => resolve(blob || null), 'image/avif', 0.9);
      } catch {
        resolve(null);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };

    img.src = objectUrl;
  });
}
